import { useEffect, useState, useRef } from "react";
import { GET } from "../utils/api";
import { CITY_DETAILS_BASED_ON_GEOLOCATION } from "../utils/constants";

const GEOLOCATION_CACHE_KEY = 'fluturo_geolocation';
const CACHE_EXPIRY_HOURS = 24;

const getCachedGeolocation = () => {
  try {
    const cached = localStorage.getItem(GEOLOCATION_CACHE_KEY);
    if (cached) {
      const { data, timestamp } = JSON.parse(cached);
      const now = Date.now();
      const hoursElapsed = (now - timestamp) / (1000 * 60 * 60);
      
      if (hoursElapsed < CACHE_EXPIRY_HOURS) {
        return data;
      } else {
        localStorage.removeItem(GEOLOCATION_CACHE_KEY);
      }
    }
  } catch (error) {
    console.error('Error reading geolocation cache:', error);
  }
  return null;
};

const setCachedGeolocation = (data) => {
  try {
    const cacheData = {
      data,
      timestamp: Date.now()
    };
    localStorage.setItem(GEOLOCATION_CACHE_KEY, JSON.stringify(cacheData));
  } catch (error) {
    console.error('Error caching geolocation data:', error);
  }
};

export const useGeolocation = () => {
  const [geolocation, setGeolocation] = useState(null);
  const hasRequestedRef = useRef(false);

  useEffect(() => {
    if (hasRequestedRef.current) return;
    hasRequestedRef.current = true;

    const cachedData = getCachedGeolocation();
    if (cachedData) {
      setGeolocation(cachedData);
      return;
    }

    const geo = async () => {
      try {
        await navigator.permissions.query({ name: "geolocation" });
        const position = await getLocation();
        if (position) {
          const { coords } = position;
          const cityDetails = await GET(
            CITY_DETAILS_BASED_ON_GEOLOCATION(coords.latitude, coords.longitude),
            {},
            ""
          );
          const code = cityDetails?.countryName;
          const newGeo = {
            latitude: coords.latitude,
            longitude: coords.longitude,
            city: code,
          };
          
          setCachedGeolocation(newGeo);
          setGeolocation(newGeo);
          return;
        }
      } catch (error) {
        console.error("Browser geolocation failed:", error);
      }
    
      const fallbackGeo = await getIPGeolocation();
      if (fallbackGeo) {
        setCachedGeolocation(fallbackGeo);
        setGeolocation(fallbackGeo);
      }
    };

    geo();
  }, []);

  return { geolocation };
};

let cachedLocation;
const getLocation = async () => {
  const options = {
    enableHighAccuracy: true,
    timeout: 5000,
    maximumAge: 0,
  };

  if (cachedLocation) {
    return Promise.resolve(cachedLocation);
  }

  return new Promise((resolve, reject) => {
    navigator.geolocation.getCurrentPosition(resolve, reject, options);
    setTimeout(() => {
      reject("Timeout exceeded");
    }, options.timeout);
  })
    .then((position) => {
      cachedLocation = position;
      return position;
    })
    .catch((error) => {
      console.error("getLocation error:", error);
      throw error;
    });
};

const getIPGeolocation = async () => {
  try {
    const response = await fetch("https://ipapi.co/json/");
    const data = await response.json();
    return {
      latitude: data.latitude,
      longitude: data.longitude,
      city: data.country,
    };
  } catch (error) {
    console.error("IP geolocation failed:", error);
    return null;
  }
};
